import type { StrictOmit, Unit } from '@enmon/commons';
import { Commodity } from '@enmon/commons/lib/shared';
import type { MessageDescriptor } from 'react-intl';
import type { IconProps } from '@/components/icon';
import type { GradientColorItem } from '@/components/Chart/colors/utils/getColorItemsByCommodity';
import { COMMODITY_TRANSLATED, COMMODITY_TRANSLATED_SHORT } from '@/common/messages';
import { GLOBAL_CHART_COLOR_PALETTE } from '@/components/Chart/colors/constants/globalChartColorPalette';

export const CALORIC_VALUE = 10.7;

export interface CommodityConfigItem {
  iconString: IconProps['iconString'];
  color: string;
  /** Display order, lower shown first (Electricity → Other). */
  order: number;
  /** Legacy weighting, higher = more important */
  priority: number;
  message: MessageDescriptor;
  shortMessage: MessageDescriptor;
  notifications?: string[];
  units?: Unit[] | null;
}

const { blue, gray, orange, purple, yellow } = GLOBAL_CHART_COLOR_PALETTE;

export const commoditiesConfig: Record<
  Commodity,
  StrictOmit<CommodityConfigItem, 'color'> & { color: GradientColorItem }
> = {
  [Commodity.Electricity]: {
    iconString: 'electricity',
    color: {
      primary: orange['500'],
      previous: { 0: orange['700'], 1: orange['300'], 2: orange['500'] },
    },
    order: 1,
    priority: 5,
    message: COMMODITY_TRANSLATED[Commodity.Electricity],
    shortMessage: COMMODITY_TRANSLATED_SHORT[Commodity.Electricity],
    notifications: ['QUARTERMAX', 'BUSINESSHOURMAX'],
  },
  [Commodity.NaturalGas]: {
    iconString: 'gas',
    color: {
      primary: yellow['500'],
      previous: { 0: yellow['700'], 1: yellow['300'], 2: yellow['500'] },
    },
    order: 2,
    priority: 4,
    message: COMMODITY_TRANSLATED[Commodity.NaturalGas],
    shortMessage: COMMODITY_TRANSLATED_SHORT[Commodity.NaturalGas],
  },
  [Commodity.Thermal]: {
    iconString: 'heat',
    color: {
      primary: purple['500'],
      previous: { 0: purple['700'], 1: purple['300'], 2: purple['500'] },
    },
    order: 3,
    priority: 3,
    message: COMMODITY_TRANSLATED[Commodity.Thermal],
    shortMessage: COMMODITY_TRANSLATED_SHORT[Commodity.Thermal],
  },
  [Commodity.Water]: {
    iconString: 'water',
    color: {
      primary: blue['500'],
      previous: { 0: blue['700'], 1: blue['300'], 2: blue['500'] },
    },
    order: 4,
    priority: 2,
    message: COMMODITY_TRANSLATED[Commodity.Water],
    shortMessage: COMMODITY_TRANSLATED_SHORT[Commodity.Water],
  },
  [Commodity.Other]: {
    iconString: 'meteo',
    color: {
      primary: gray['500'],
      previous: { 0: gray['700'], 1: gray['300'], 2: gray['500'] },
    },
    order: 5,
    priority: 1,
    message: COMMODITY_TRANSLATED[Commodity.Other],
    shortMessage: COMMODITY_TRANSLATED_SHORT[Commodity.Other],
  },
};

export type CommoditiesConfigKey = keyof typeof commoditiesConfig;

export const getCommodityIndex = (commodity: Commodity): number => commoditiesConfig[commodity].order;
